import { Check, Lock } from 'lucide-react';
import type { ButtonHTMLAttributes } from 'react';

export interface ToggleChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'disabled'> {
  /** Texto visible del chip */
  label: string;
  /** Estado de selección (controlado por el padre) */
  active: boolean;
  /** Se dispara al hacer click / Enter / Space. No se llama si `locked` es true. */
  onToggle?: () => void;
  /**
   * Marca el chip como obligatorio / no editable (ej. columnas que la
   * tabla siempre necesita). Cuando es true: se deshabilita de forma
   * nativa, muestra ícono de candado en vez de check, y aplica estilo
   * apagado — sin importar el valor de `active`.
   */
  locked?: boolean;
  /** Texto del tooltip cuando `locked` es true */
  lockedTooltip?: string;
}

/**
 * ToggleChip — pill de selección múltiple (patrón "filter chip", Material
 * Design), con soporte para variante `locked`.
 *
 * Componente genérico de la librería `contasis`: no sabe nada de
 * "columnas", "productos" ni de negocio. Úsalo en cualquier lugar de
 * Senciyo donde el usuario elija uno o varios criterios — columnas
 * visibles, categorías, etiquetas, filtros de reportes, etc.
 *
 * Distinto de `FilterChip` (tag removible con botón "x" para filtros ya
 * aplicados) y de `ToggleButton` (un único botón on/off): este es para
 * un grupo de opciones donde cada pill se prende o apaga por sí sola.
 */
export function ToggleChip({
  label,
  active,
  onToggle,
  locked = false,
  lockedTooltip = 'Columna obligatoria, siempre visible en la tabla',
  className = '',
  ...rest
}: ToggleChipProps) {
  const showIcon = locked || active;
  const Icon = locked ? Lock : Check;
  const iconSize = locked ? 10 : 11;
  const iconBoxWidth = locked ? 'w-[10px]' : 'w-[11px]';

  const padding = showIcon ? 'pl-[9px] pr-3' : 'px-3';
  const colors = locked
    ? 'cursor-not-allowed border-muted bg-surface-2 text-muted'
    : active
      ? 'border-brand bg-brand-light text-brand active:scale-95'
      : 'border-[var(--border-default)] bg-surface-0 text-secondary hover:border-strong hover:bg-surface-hover active:scale-95';

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={locked ? true : active}
      disabled={locked}
      title={locked ? lockedTooltip : undefined}
      onClick={locked ? undefined : () => onToggle?.()}
      className={[
        'inline-flex h-[26px] items-center whitespace-nowrap rounded-full border text-caption-medium transition-colors duration-150',
        padding,
        colors,
        className,
      ].join(' ')}
      {...rest}
    >
      <span
        aria-hidden
        className={[
          'inline-flex items-center justify-center overflow-hidden transition-[width,opacity,margin] duration-150',
          showIcon ? `mr-[5px] ${iconBoxWidth} opacity-100` : 'mr-0 w-0 opacity-0',
          locked ? 'text-muted' : 'text-brand',
        ].join(' ')}
      >
        <Icon size={iconSize} strokeWidth={locked ? 2.2 : 3} />
      </span>
      {label}
    </button>
  );
}
